/**
 * @file Wire schemas for the admin Recalibration panel — the
 *       mentor-capture signal, the model-drafted recalibration
 *       proposals, and the content-PR instruction artifact.
 * @module @labcraft/admin/shared/recalibration_routes
 *
 * Umbrella #1417 Phase 7, part #1433. The recalibration slice moved
 * out of `@labcraft/lab` and into admin here: the operator's steering
 * surface (review the anonymized capture signal, ask the model for a
 * draft, approve a subset, emit a content-PR instruction) is an
 * admin-console concern, not a lab-daemon one.
 *
 * Three wire families live here:
 *
 *   1. The **signal** shapes — the read-only aggregate the
 *      `@labcraft/postgres` `MentorCaptureReport` produces
 *      (`DiagnosisOutcomeStat` + `WrongTurnStat`), mirrored as Zod so
 *      the panel can render it. `lastFiredAt` rides the wire as an
 *      ISO-8601 string (JSON has no `Date`), matching the
 *      `courses_routes.ts` convention where DB `Date`s serialize to
 *      ISO strings and the panel formats them client-side.
 *   2. The **draft** shapes — the reviewable proposal set the model
 *      returns (relocated verbatim from lab's `service/recalibration.ts`
 *      so the service + client + UI share one definition).
 *   3. The **content-PR** artifact — the deterministic instruction set
 *      the operator applies from a devshell to actuate an approved
 *      draft (daemons hold no git checkout, so the artifact emits
 *      edit instructions, not a raw patch).
 *
 * Every route path starts with `/api/recalibration`; paths are
 * wire-true (absolute), matching how `courses_routes.ts` binds
 * `/api/courses/*`.
 */

import { defineRoute } from '@labcraft/core'
import { MentorDispositionSchema } from '@labcraft/mentor/shared'
import { z } from 'zod'

////////////////////////////////////////////////////////////////////////////////
// Signal shapes (mirror @labcraft/postgres mentor-capture aggregates)
////////////////////////////////////////////////////////////////////////////////

/**
 * Disposition of a captured observation. Re-exports the canonical
 * `MentorDispositionSchema` — the single source of truth the mentor daemon
 * keys the `mentor_interactions.disposition` column on — rather than a
 * hand-rolled parallel enum. The parallel version silently drifted and 500'd
 * this panel from Aug 12 2026 onward, when the mentor's `held` (Aug 11) and
 * `encouragement` (Aug 13) dispositions landed in the capture data but were
 * absent from admin's copy, failing `RecalibrationSignalResponseSchema.parse`.
 * Sourcing the enum from the mentor wire contract makes that drift impossible
 * by construction (`.agents/rules/design.md` § "Derive types from schemas").
 */
export const CaptureDispositionSchema = MentorDispositionSchema

/** Lab-derived episode outcome per (session, objective). */
export const CaptureOutcomeSchema = z.enum(['RECOVERED', 'UNKNOWN'])

/** Course-kit `WrongTurn.origin`. */
export const WrongTurnOriginSchema = z.enum(['authored', 'promoted'])

/**
 * One aggregated diagnosis+outcome bucket — the primary recalibration
 * signal. Mirrors `@labcraft/postgres` `DiagnosisOutcomeStat`.
 *
 * @property course - Course slug.
 * @property part - Part id within the course.
 * @property objectiveId - Opaque objective key.
 * @property disposition - The observation disposition.
 * @property outcome - Episode outcome (`RECOVERED`/`UNKNOWN`).
 * @property errorClass - Diagnosed error class, or `null` (non-nudge).
 * @property count - Rows in this bucket.
 */
export const DiagnosisOutcomeStatSchema = z.object({
  course: z.string(),
  part: z.string(),
  objectiveId: z.string(),
  disposition: CaptureDispositionSchema,
  outcome: CaptureOutcomeSchema,
  errorClass: z.string().nullable(),
  count: z.number(),
})

/** Parsed `DiagnosisOutcomeStat`. */
export type DiagnosisOutcomeStat = z.infer<typeof DiagnosisOutcomeStatSchema>

/**
 * One aggregated wrong-turn-firing bucket — the retire/revalidate
 * view. Mirrors `@labcraft/postgres` `WrongTurnStat`, with
 * `lastFiredAt` as an ISO-8601 string on the wire.
 *
 * @property course - Course slug.
 * @property part - Part id within the course.
 * @property objectiveId - Opaque objective key.
 * @property wrongTurnId - The authored `WrongTurn.id`.
 * @property origin - `authored` / `promoted`.
 * @property firings - Total firings recorded.
 * @property lastFiredAt - Most recent firing, ISO-8601 string.
 */
export const WrongTurnStatSchema = z.object({
  course: z.string(),
  part: z.string(),
  objectiveId: z.string(),
  wrongTurnId: z.string(),
  origin: WrongTurnOriginSchema,
  firings: z.number(),
  lastFiredAt: z.string(),
})

/** Parsed `WrongTurnStat`. */
export type WrongTurnStat = z.infer<typeof WrongTurnStatSchema>

/**
 * Both signals the recalibration proposer consumes, wire-shaped.
 *
 * @property diagnosis - Per course/part/objective diagnosis+outcome
 *   buckets, most-frequent-first.
 * @property wrongTurns - Per-rule firing buckets (retire/revalidate
 *   view), most-frequent-first.
 */
export const RecalibrationSignalResponseSchema = z.object({
  diagnosis: z.array(DiagnosisOutcomeStatSchema),
  wrongTurns: z.array(WrongTurnStatSchema),
})

/** Parsed `RecalibrationSignalResponse`. */
export type RecalibrationSignalResponse = z.infer<
  typeof RecalibrationSignalResponseSchema
>

////////////////////////////////////////////////////////////////////////////////
// Draft artifact shape (relocated from @labcraft/lab/service/recalibration)
////////////////////////////////////////////////////////////////////////////////

/**
 * The kind of recalibration a proposal recommends. `promote-misconception`
 * — turn a frequent model-judged misconception into a deterministic
 * authored `WrongTurn`; `retire-rule` — a promoted/authored rule went
 * quiet; `revalidate-rule` — a rule keeps firing (keep it); `clarify-
 * objective` — the objective's authored context needs work (low recovery).
 */
export const RecalibrationClassSchema = z.enum([
  'promote-misconception',
  'retire-rule',
  'revalidate-rule',
  'clarify-objective',
])

/** Parsed `RecalibrationClass`. */
export type RecalibrationClass = z.infer<typeof RecalibrationClassSchema>

/**
 * One proposed recalibration. `targetRuleId` is the `WrongTurn.id` for
 * rule retire/revalidate proposals, `null` otherwise.
 *
 * @property recalibrationClass - The kind of change recommended.
 * @property course - Course slug the proposal targets.
 * @property part - Part id within the course.
 * @property objectiveId - Opaque objective key the change lives under.
 * @property targetRuleId - `WrongTurn.id` for retire/revalidate, else null.
 * @property rationale - Why the signal warrants this change.
 * @property proposedChange - The concrete edit the operator should make.
 */
export const RecalibrationProposalSchema = z.object({
  recalibrationClass: RecalibrationClassSchema,
  course: z.string(),
  part: z.string(),
  objectiveId: z.string(),
  targetRuleId: z.string().nullable(),
  rationale: z.string(),
  proposedChange: z.string(),
})

/** Parsed `RecalibrationProposal`. */
export type RecalibrationProposal = z.infer<typeof RecalibrationProposalSchema>

/**
 * The whole draft — a reviewable set of proposals.
 *
 * @property proposals - Every proposed recalibration; empty when the
 *   signal warrants no change.
 */
export const RecalibrationDraftSchema = z.object({
  proposals: z.array(RecalibrationProposalSchema),
})

/** Parsed `RecalibrationDraft`. */
export type RecalibrationDraft = z.infer<typeof RecalibrationDraftSchema>

////////////////////////////////////////////////////////////////////////////////
// Content-PR artifact
////////////////////////////////////////////////////////////////////////////////

/**
 * The content-PR instruction artifact — what the operator applies from
 * a devshell to actuate an approved draft. Deterministic + pure (built
 * by `Recalibration.contentPrArtifact`) so it is unit-testable and
 * never re-runs the model.
 *
 * @property title - One-line PR title summarizing the draft.
 * @property body - Markdown PR body: an intro plus one section per
 *   approved proposal naming the exact file/objective/rule to edit.
 * @property proposals - The approved proposals the body describes,
 *   echoed for downstream tooling.
 */
export const ContentPrArtifactSchema = z.object({
  title: z.string(),
  body: z.string(),
  proposals: z.array(RecalibrationProposalSchema),
})

/** Parsed `ContentPrArtifact`. */
export type ContentPrArtifact = z.infer<typeof ContentPrArtifactSchema>

////////////////////////////////////////////////////////////////////////////////
// Routes
////////////////////////////////////////////////////////////////////////////////

/**
 * `GET /api/recalibration/signal` — read the anonymized mentor-capture
 * aggregate (diagnosis+outcome buckets + wrong-turn firing stats).
 * Behind the admin session cookie; 401 for anonymous requests.
 */
export const RecalibrationSignalRoute = defineRoute({
  method: 'GET',
  path: '/api/recalibration/signal',
  response: RecalibrationSignalResponseSchema,
  description: 'Read the anonymized mentor-capture recalibration signal.',
})

/**
 * `POST /api/recalibration/draft` — ask the model to draft
 * recalibration proposals from the current signal. Requestless (the
 * daemon reads the signal itself). Behind the admin session cookie;
 * 401 anonymous, 503 when no agent has been applied, 502 on a model
 * error.
 */
export const RecalibrationDraftRoute = defineRoute({
  method: 'POST',
  path: '/api/recalibration/draft',
  response: RecalibrationDraftSchema,
  description: 'Draft recalibration proposals from the capture signal.',
})

/**
 * `POST /api/recalibration/content-pr` — turn an approved draft into a
 * content-PR instruction artifact. The request body is the approved
 * subset of proposals; the response is the deterministic artifact.
 * Behind the admin session cookie; 401 for anonymous requests.
 */
export const RecalibrationContentPrRoute = defineRoute({
  method: 'POST',
  path: '/api/recalibration/content-pr',
  request: RecalibrationDraftSchema,
  response: ContentPrArtifactSchema,
  description: 'Emit a content-PR instruction artifact from an approved draft.',
})
